/**
 * I profili di gioco: come si legano a giochi e piattaforme, e cosa manca per iscriversi.
 *
 * Il profilo di gioco e' il **prerequisito dell'iscrizione**: la scheda torneo dichiara
 * `gameProfileRequired` fra i requisiti, e senza il profilo del gioco giusto l'iscrizione non
 * parte. Questa e' la ragione per cui la schermata esiste prima di quella d'iscrizione.
 *
 * 🔴 **LA TRAPPOLA DELLA GIUNZIONE, ed e' la stessa che e' costata due PR (#122, #123).**
 * `LimuloGame` porta DUE identificatori — `id` (l'ObjectId) e `gameId` (il codice di
 * business) — e `LimuloProfile.gameId` deve legarsi al secondo (kb#81: il valore in
 * passthrough e' il CODICE). Ma i due campi hanno nomi che si somigliano, la giunzione
 * sbagliata non da' nessun errore, e il risultato e' una lista di profili che non trovano
 * mai il loro gioco: ogni riga mostra un'etichetta grezza e nessuno se ne accorge finche'
 * qualcuno non conta.
 *
 * ⇒ Qui la giunzione prova `gameId` **e poi** `id`, e chi non trova nulla **non sparisce**:
 * la riga resta col codice grezzo. Un profilo che sparisce da una lista di profili e' un
 * difetto peggiore di uno mostrato male — il secondo si vede, il primo no.
 */

import { COC_NAME_RE, CR_NAME_RE } from '../gameFeatures.ts';
import type { LimuloGame, LimuloPlatform, LimuloProfile } from '../../types/limulo.ts';

/**
 * L'etichetta leggibile di un gioco o di una piattaforma.
 *
 * ⚠️ L'ordine e' `label` → `name` → il codice, e il codice NON e' un ripiego imbarazzante:
 * e' l'unica cosa che sicuramente identifica la riga. Meglio «clash-royale» che «—», perche'
 * il primo si puo' cercare e il secondo no.
 */
export function labelOf(
  entity: { label?: string; name?: string } | null | undefined,
  fallbackCode: string,
): string {
  return entity?.label?.trim() || entity?.name?.trim() || fallbackCode;
}

/** Il gioco di un codice (`gameId` di un torneo o di un profilo), o `null` se nessuno combacia. */
export function gameByCode(code: string, games: readonly LimuloGame[]): LimuloGame | null {
  if (!code) return null;
  // Prima il codice di business (kb#81), poi l'ObjectId. L'ordine conta: su un insieme in cui
  // per sfortuna un `id` di un gioco combaciasse col `gameId` di un altro, la risposta giusta
  // e' quella per codice.
  return games.find((g) => g.gameId === code) ?? games.find((g) => g.id === code) ?? null;
}

/** Il gioco di un profilo, o `null` se nessun gioco noto combacia. */
export function gameOfProfile(
  profile: Pick<LimuloProfile, 'gameId'>,
  games: readonly LimuloGame[],
): LimuloGame | null {
  return gameByCode(profile.gameId, games);
}

/**
 * Il NOME di un gioco dal suo codice, per mostrarlo (la card del torneo, eFootball I-201).
 *
 * 🔑 Il ripiego e' il codice GREZZO, com'era la card prima: se il catalogo non ha risposto o il
 * gioco non c'e', si mostra cio' che il BFF ha mandato, e mai una parola inventata come
 * «Sconosciuto» — il torneo il suo gioco ce l'ha, siamo noi a non saperlo nominare.
 * ⚠️ Supera il «passthrough» del vincolo kb#81 per la sola ETICHETTA: il legame codice →
 * catalogo e' gia' in esercizio (lobby backgammon, scheda dell'incontro, azione persistente),
 * e il codice resta quello che il torneo porta.
 */
export function gameNameOf(code: string, games: readonly LimuloGame[] | null | undefined): string {
  return labelOf(gameByCode(code, games ?? []), code);
}

/** La piattaforma di un profilo, con la stessa regola dei due identificatori. */
export function platformOfProfile(
  profile: Pick<LimuloProfile, 'platformId'>,
  platforms: readonly LimuloPlatform[],
): LimuloPlatform | null {
  const code = profile.platformId;
  if (!code) return null;
  return (
    platforms.find((p) => p.platformId === code) ?? platforms.find((p) => p.id === code) ?? null
  );
}

/**
 * I giochi per cui NON esiste un profilo — cioe' i tornei a cui oggi non ci si puo' iscrivere.
 *
 * ⚠️ **Un elenco di giochi vuoto NON significa «hai tutti i profili».** Se `/v1/games` non ha
 * risposto, la risposta onesta e' «non lo so», e chi chiama deve distinguerla: per questo la
 * funzione prende i giochi gia' caricati e chi la usa non deve renderne il vuoto come una
 * conferma. Ottimismo silenzioso su un errore di rete e' il fake-empty che questa sezione
 * esiste per non avere.
 */
export function gamesWithoutProfile(
  games: readonly LimuloGame[],
  profiles: readonly LimuloProfile[],
): LimuloGame[] {
  const coperti = new Set(profiles.map((p) => p.gameId).filter((c) => c !== ''));
  // ⚠️ Si confrontano ENTRAMBE le chiavi del gioco, ed e' questo — non un secondo giro di
  // risoluzione — a far si' che un profilo legato per ObjectId copra lo stesso gioco di uno
  // legato per codice.
  //
  // 📌 Qui c'era un blocco che risolveva ogni profilo con `gameOfProfile` e ne aggiungeva
  // entrambe le chiavi. Sembrava la difesa di quel caso, e aveva perfino la sua asserzione
  // verde. **Era codice morto**: `gameOfProfile` combacia solo su `gameId` o `id`, cioe'
  // esattamente le due chiavi gia' controllate qui sotto. L'ha mostrato una MUTAZIONE —
  // toglierlo non ha fatto arrossire niente. *Un test verde accanto a una riga non prova che
  // sia quella riga a proteggerlo.*
  return games.filter((g) => !coperti.has(g.gameId) && !coperti.has(g.id));
}

/**
 * Il gioco «Clash Royale» fra quelli che Limulo dichiara — **per NOME, mai per id**.
 *
 * ⚠️ Gli id non attraversano i sistemi: `GET /v1/games` restituisce ObjectId, e il
 * selettore di gioco della TMA ragiona sull'id numerico del monolite (`CR_GAME_ID = 6`).
 * Cercare per id qui darebbe SEMPRE `null`, e un `null` che sembra «non c'e' il gioco»
 * invece di «ho cercato con la chiave sbagliata» e' esattamente il tipo di vuoto che
 * questa sezione esiste per non affermare.
 *
 * ⛔ CoC prima di CR, come in `featuresForGame`: «Clash of Clans» contiene «Clash» e
 * senza questo ordine finirebbe nel matcher di Clash Royale.
 */
export function limuloClashRoyaleGame(
  games: readonly LimuloGame[] | null | undefined,
): LimuloGame | null {
  if (!games) return null;
  return (
    games.find((g) => {
      const nome = g.name || g.label || '';
      return !COC_NAME_RE.test(nome) && CR_NAME_RE.test(nome);
    }) ?? null
  );
}

/**
 * Il profilo dell'utente per un gioco, se c'e'.
 *
 * Confronta `profile.gameId` sia col codice sia con l'`_id` del gioco, per la stessa
 * ragione (e nello stesso ordine) di `gameOfProfile` qui sopra: la facciata usa l'uno o
 * l'altro a seconda della rotta, e chi chiama non puo' sapere quale gli e' arrivato.
 */
export function limuloProfileForGame(
  profiles: readonly LimuloProfile[] | null | undefined,
  game: LimuloGame | null | undefined,
): LimuloProfile | null {
  return limuloProfilesForGame(profiles, game)[0] ?? null;
}

/**
 * TUTTI i profili dell'utente per un gioco (stessa giunzione, stesso ordine di chiavi di
 * `limuloProfileForGame`, che ne prende il primo). Serve a chi deve accorgersi che sono PIU' DI UNO:
 * «il primo» e' una scelta per ordine di lista, e QBot la rifiuta (`limuloTag.ts`, #251).
 */
export function limuloProfilesForGame(
  profiles: readonly LimuloProfile[] | null | undefined,
  game: LimuloGame | null | undefined,
): LimuloProfile[] {
  if (!profiles || !game) return [];
  return profiles.filter(
    (p) => p.gameId !== '' && (p.gameId === game.gameId || p.gameId === game.id),
  );
}

/**
 * «La proprietà di questo account di gioco è stata PROVATA?»
 *
 * 🔴 **IL PREDICATO È `ownershipVerified`, MAI `profileValidation.valid`** — e il campo dal
 * nome più invitante è quello sbagliato.
 * [R `referto-disputa-orc` via `or0`, letto il 2026-09-10 su un profilo vero di `stg-tma`]
 * la busta risponde **`valid: true`** *mentre* **`ownershipVerified: false`**, con
 * `unverified_allowed` e «Clash Royale API non configurata». ⇒ `valid` dice *«la validazione
 * è passata sotto una policy che ammette profili NON verificati»*, **non** *«la proprietà è
 * provata»*. Una riga «verificato» costruita su `valid` direbbe il falso su ogni profilo non
 * verificato — **e lo direbbe nel verso che rassicura**.
 *
 * ⚠️ **IL RAMO `true` NON È MAI STATO OSSERVATO.** Su staging `ownershipVerified` è falso
 * perché la chiave Clash Royale dell'organizzazione **non è configurata**: nessuno ha mai
 * visto un `true` uscire da questa busta. ⇒ *Chi legge questa funzione non deve credere che
 * quel ramo sia provato: è scritto, non esercitato.*
 * ⚠️ E la misura è **un profilo, un ambiente, un istante**.
 *
 * ⛔ Legge la forma in modo difensivo perché `LimuloProfile.profileValidation` è tipizzato
 * `unknown` e **la nidificazione esatta viene da UNA lettura riportata**: se il campo stesse
 * altrove, questa funzione risponde `false`. È il verso giusto in cui sbagliare — *un
 * «non verificato» di troppo si vede e si corregge; un «verificato» falso no.*
 */
export function limuloProfileOwnershipVerified(
  profile: LimuloProfile | null | undefined,
): boolean {
  if (!profile) return false;
  const v = profile.profileValidation;
  if (typeof v !== 'object' || v === null) return false;
  // `=== true` e non un truthy: `"false"`, `1` o un oggetto non sono una prova.
  return (v as Record<string, unknown>).ownershipVerified === true;
}
